/**
 * Care Background Agent · frontend/src/components/SessionList.tsx
 * Session history with search, filter by status, and "My Sessions" toggle.
 * Addresses P5.05 — Session Search / History
 */

import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

interface Session {
  id: string;
  thread_id: string;
  status: string;
  task_title?: string;
  initiating_name?: string;
  initiating_email?: string;
  pr_url?: string;
  confidence_level?: string;
  created_at: string;
  completed_at?: string;
  environment?: string;
  summary_json?: string;
}

const API = import.meta.env.VITE_WORKER_URL ?? "";
const CARE_SECRET = import.meta.env.VITE_CARE_SECRET ?? "";

const STATUS_OPTIONS = ["all", "active", "booting", "completed", "suspended", "failed"];

const STATUS_COLORS: Record<string, string> = {
  active:    "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
  booting:   "text-blue-400 bg-blue-500/10 border-blue-500/20",
  pending:   "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  completed: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  suspended: "text-gray-400 bg-gray-500/10 border-gray-500/20",
  failed:    "text-red-400 bg-red-500/10 border-red-500/20",
};

export default function SessionList() {
  const { user } = useAuth0();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [mineOnly, setMineOnly] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (mineOnly && user?.email) {
        params.set("email", user.email);
      }

      const res = await fetch(`${API}/api/sessions?${params}`, {
        headers: { "X-Care-Secret": CARE_SECRET },
      });
      const data = await res.json();
      setSessions(data.sessions ?? []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, debouncedSearch, mineOnly, user]);

  useEffect(() => {
    fetchSessions();
    const t = setInterval(fetchSessions, 15000);
    return () => clearInterval(t);
  }, [fetchSessions]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    const now = Date.now();
    const diff = now - d.getTime();
    if (diff < 60000) return "just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  const getConfidenceColor = (c?: string) => {
    if (c === "high") return "text-emerald-400";
    if (c === "medium") return "text-yellow-400";
    if (c === "low") return "text-red-400";
    return "text-gray-400";
  };

  return (
    <div className="min-h-screen bg-[#0f1117] text-white">
      <header className="border-b border-[#2a2e45] bg-[#181c2a] sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#5b4dff] to-[#8b5cf6] flex items-center justify-center text-white font-bold text-sm">
              C
            </div>
            <div>
              <div className="font-semibold">Care</div>
              <div className="text-[#8b8fa8] text-xs">Vitlycare Background Agent</div>
            </div>
          </div>
          {user && (
            <div className="flex items-center gap-2 text-sm text-[#8b8fa8]">
              {user.picture && (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
              )}
              {user.name}
            </div>
          )}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8b8fa8] text-sm">🔍</span>
            <input
              type="text"
              placeholder="Search sessions by task, file, PR…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-[#181c2a] border border-[#2a2e45] rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-[#8b8fa8] focus:outline-none focus:border-[#5b4dff] transition-colors"
            />
          </div>

          <div className="flex gap-1 bg-[#181c2a] border border-[#2a2e45] rounded-lg p-1">
            {STATUS_OPTIONS.map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1 rounded text-xs capitalize transition-colors ${
                  statusFilter === s
                    ? "bg-[#5b4dff] text-white"
                    : "text-[#8b8fa8] hover:text-white"
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          <button
            onClick={() => setMineOnly(!mineOnly)}
            className={`px-4 py-2 rounded-lg border text-sm transition-colors ${
              mineOnly
                ? "bg-[#5b4dff]/20 border-[#5b4dff] text-[#5b4dff]"
                : "bg-[#181c2a] border-[#2a2e45] text-[#8b8fa8] hover:text-white"
            }`}
          >
            My Sessions
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {["active", "completed", "suspended", "failed"].map((s) => {
            const count = sessions.filter((ss) => ss.status === s).length;
            return (
              <div
                key={s}
                className="bg-[#181c2a] border border-[#2a2e45] rounded-xl p-3 cursor-pointer hover:border-[#3a3e55] transition-colors"
                onClick={() => setStatusFilter(s === statusFilter ? "all" : s)}
              >
                <div className={`text-xl font-bold ${STATUS_COLORS[s]?.split(" ")[0] ?? "text-white"}`}>
                  {count}
                </div>
                <div className="text-[#8b8fa8] text-xs capitalize mt-0.5">{s}</div>
              </div>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48 text-[#8b8fa8]">
            <div className="w-6 h-6 border-2 border-[#5b4dff] border-t-transparent rounded-full animate-spin mr-3" />
            Loading sessions…
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-16 text-[#8b8fa8]">
            <div className="text-4xl mb-3">🤖</div>
            <div className="text-sm">
              {search || statusFilter !== "all" || mineOnly
                ? "No sessions match your filters."
                : "No Care sessions yet. Mention @Care in Google Chat to start one."}
            </div>
          </div>
        ) : (
          <div className="grid gap-3">
            {sessions.map((s) => {
              let summary: Record<string, unknown> | null = null;
              try {
                if (s.summary_json) summary = JSON.parse(s.summary_json);
              } catch {}

              return (
                <Link
                  key={s.id}
                  to={`/session/${s.id}`}
                  className="block bg-[#181c2a] border border-[#2a2e45] rounded-xl p-4 hover:border-[#5b4dff]/50 transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className={`px-2 py-0.5 rounded-full border text-xs capitalize ${STATUS_COLORS[s.status] ?? ""}`}
                        >
                          {s.status}
                        </span>
                        {s.environment === "staging" && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-orange-500/10 text-orange-400 border border-orange-500/20">
                            staging
                          </span>
                        )}
                        {summary?.confidence_level && (
                          <span className={`text-xs ${getConfidenceColor(summary.confidence_level as string)}`}>
                            {summary.confidence_level as string} confidence
                          </span>
                        )}
                      </div>

                      <div className="text-white text-sm font-medium truncate group-hover:text-[#a89cff] transition-colors">
                        {s.task_title ?? s.id}
                      </div>

                      <div className="flex items-center gap-3 mt-1 text-xs text-[#8b8fa8]">
                        <span>{s.initiating_name ?? s.initiating_email}</span>
                        <span>·</span>
                        <span>{formatDate(s.created_at)}</span>
                        {s.pr_url && (
                          <>
                            <span>·</span>
                            <span className="text-[#5b4dff] truncate max-w-[160px]">
                              {s.pr_url.replace("https://github.com/", "")}
                            </span>
                          </>
                        )}
                      </div>

                      {summary?.files_changed && Array.isArray(summary.files_changed) && (
                        <div className="flex gap-1.5 mt-2 flex-wrap">
                          {(summary.files_changed as string[]).slice(0, 4).map((f) => (
                            <span
                              key={f}
                              className="px-1.5 py-0.5 bg-[#2a2e45] rounded text-xs text-[#8b8fa8] font-mono"
                            >
                              {f.split("/").pop()}
                            </span>
                          ))}
                          {(summary.files_changed as string[]).length > 4 && (
                            <span className="px-1.5 py-0.5 bg-[#2a2e45] rounded text-xs text-[#8b8fa8]">
                              +{(summary.files_changed as string[]).length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="shrink-0 text-[#5b4dff] opacity-0 group-hover:opacity-100 transition-opacity text-sm mt-1">
                      →
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
